import React, { useEffect, useState } from "react";
import SlideOutSidebar from "../../../../../ui-components/slide-out-sidebar/slideOutSidebar";
import Table from "ui-components/table/table";
import { createColumnHelper } from "@tanstack/react-table";
import { useTerminalViewPickupSummaryDetail } from "hooks/usePickupSummary";
import { TerminalViewPickupSummaryFilterListModel } from "../../../../../app/data/pickup/models";
import { PickupStatus } from "app/data/common/route";
import "../terminalViewPickup.scss";
import { Routes } from "app/route/RoutesConfig";
import { useHistory } from "react-router-dom";

interface TerminalViewPickupDetailsProps {
    terminalNumber: number;
    terminalName?: string;
    isOpen: boolean;
    onClose: () => void;
    filterData: TerminalViewPickupSummaryFilterListModel;
    data?: any[];
    initialStatus?: string;
    isLoading?: boolean;
}

const columnHelper = createColumnHelper<any>();

const TerminalViewPickupDetails: React.FC<TerminalViewPickupDetailsProps> = ({
    terminalNumber,
    terminalName,
    isOpen,
    onClose,
    filterData,
    data,
    initialStatus,
    isLoading,
}) => {

    const history = useHistory();

    const tabs = ["Created", "Completed", "Missed", "Rescheduled", "Canceled"];
    const [selectedTab, setSelectedTab] = useState(initialStatus || "Created");

    const statusMap: Record<string, PickupStatus> = {
        Created: PickupStatus.CREATED,
        Completed: PickupStatus.COMPLETED,
        Missed: PickupStatus.MISSED,
        Rescheduled: PickupStatus.RESCHEDULE_PICKUP,
        Canceled: PickupStatus.CANCELED,
    };

    const mergedFilter = {
        ...filterData,
        status: statusMap[selectedTab],
        originTerminalNumbers: undefined,
        destinationTerminalNumbers: Array.isArray(filterData.destinationTerminalNumbers)
            ? filterData.destinationTerminalNumbers.map((t) => t.value)
            : [],
        ...(filterData.timePeriod === "CUSTOM"
            ? { from: filterData.from, to: filterData.to }
            : { from: undefined, to: undefined }),
    };

    const {
        data: terminalViewDetail,
        isLoading: listLoading,
        refetch: refetchTerminalDetail,
    } = useTerminalViewPickupSummaryDetail(terminalNumber, mergedFilter, {
        enabled: false,
    });


    const tableData =
        data && Array.isArray(data) && data.length > 0
            ? data
            : terminalViewDetail?.content && Array.isArray(terminalViewDetail.content)
                ? terminalViewDetail.content
                : [];

    const columns = [
        columnHelper.accessor("formattedPickupNumber", {
            id: "formattedPickupNumber",
            header: "Pickup #",
            cell: (cellProps) => (
                <span
                    className="xgs-table-link-cell"
                    onClick={(e) => {
                        e.stopPropagation();
                        history.push(`${Routes.pickup.log}/${cellProps.getValue()}`)
                    }}>
                    {cellProps.getValue()}
                </span >
            ),
            size: 20,
        }),
        columnHelper.accessor("lastEventDate", {
            id: "lastEventDate",
            header: "Last Event Date",
            cell: (cellProps) => <>{cellProps.getValue().toUiDateTimeFormat()}</>,
            size: 50,
        }),
        columnHelper.accessor("requesterCompany", {
            id: "requesterCompany",
            header: "Requester (Company)",
            size: 70,
        }),
        columnHelper.accessor("shipperName", {
            id: "shipperName",
            header: "Shipper",
            cell: (cellProps) => (
                <span>
                    {[
                        cellProps.getValue(),
                        cellProps?.row?.original?.shipperCity,
                        cellProps?.row?.original?.shipperState
                    ]
                        .filter(Boolean)
                        .join(", ")}
                </span>
            ),
            size: 80,
        }),
        columnHelper.accessor("consigneeName", {
            id: "consigneeName",
            header: "Consignee",
            cell: (cellProps) => (
                <span>
                    {[
                        cellProps.getValue(),
                        cellProps?.row?.original?.consigneePostalCode,
                    ]
                        .filter(Boolean)
                        .join(", ")}
                </span>
            ),
            size: 70,
        }),
        columnHelper.accessor("totalUnits", {
            id: "totalUnits",
            header: "Freight Details",
            cell: (cellProps) => (
                <span>
                    Units: {cellProps.getValue()}, lbs: {cellProps?.row?.original?.totalWeight}
                </span>
            ),
            size: 50,
        }),
        columnHelper.accessor("statusNotes", {
            id: "statusNotes",
            header: "Status Notes",
            size: 40,
        }),
    ];

    useEffect(() => {
        if (isOpen && initialStatus) {
            setSelectedTab(initialStatus);
        }
    }, [initialStatus, isOpen]);

    useEffect(() => {
        if (isOpen && terminalNumber) {
            refetchTerminalDetail();
        }
    }, [isOpen, terminalNumber, selectedTab, refetchTerminalDetail]);

    return (
        <SlideOutSidebar
            header={`${terminalName ?? ""} Terminal - Pickups`}
            onClose={onClose}
            show={isOpen}
            size="large"
        >
            <div className="tab-wrapper_terminalview_detail">
                <div className="tab-container">
                    {tabs.map((tab, index) => (
                        <div
                            key={tab}
                            onClick={() => setSelectedTab(tab)}
                            className={`tab-item ${selectedTab === tab ? 'active' : ''} ${index === 0 ? 'first' : index === tabs.length - 1 ? 'last' : ''
                                }`}
                        >
                            {tab}
                        </div>
                    ))}
                </div>
                <div>
                    {selectedTab === "Created" ? (
                        <Table
                            isLoading={isLoading ?? listLoading}
                            columns={columns}
                            data={tableData}
                            rowHeight={58}
                            minTableHeight={220}
                            noResultsText={`No Pickup Summary ${selectedTab} List`}
                            infiniteScroll
                            responsive
                        />
                    ) : selectedTab === "Completed" ? (
                        <Table
                            isLoading={isLoading ?? listLoading}
                            columns={columns}
                            data={tableData}
                            rowHeight={58}
                            minTableHeight={220}
                            noResultsText={`No Pickup Summary ${selectedTab} List`}
                            infiniteScroll
                            responsive
                        />
                    ) : selectedTab === "Missed" ? (
                        <Table
                            isLoading={isLoading ?? listLoading}
                            columns={columns}
                            data={tableData}
                            rowHeight={58}
                            minTableHeight={220}
                            noResultsText={`No Pickup Summary ${selectedTab} List`}
                            infiniteScroll
                            responsive
                        />
                    ) : selectedTab === "Rescheduled" ? (
                        <Table
                            isLoading={isLoading ?? listLoading}
                            columns={columns}
                            data={tableData}
                            rowHeight={58}
                            minTableHeight={220}
                            noResultsText={`No Pickup Summary ${selectedTab} List`}
                            infiniteScroll
                            responsive
                        />
                    ) : selectedTab === "Canceled" ? (
                        <Table
                            isLoading={isLoading ?? listLoading}
                            columns={columns}
                            data={tableData}
                            rowHeight={58}
                            minTableHeight={220}
                            noResultsText={`No Pickup Summary ${selectedTab} List`}
                            infiniteScroll
                            responsive
                        />
                    ) : null}
                </div>
            </div>
        </SlideOutSidebar>
    );
};

export default TerminalViewPickupDetails;